import { cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { Fragment, type ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDay } from "@/lib/utils/date";
import {
  stickyHeadFirstRowClass,
  stickyHeadSecondRowClass,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";
import { ClasseurFilterBar, type FilterDef } from "../components/classeur";
import type { FilterValues } from "../components/classeur/types";
import { useClasseurFilters } from "../components/classeur/use-classeur-data";
import { PdfViewer, type PdfViewerFile } from "../components/pdf-viewer";

// Écran 010 — historique des réceptions de documents d'exécution : une ligne
// par version reçue, regroupée par émetteur puis par transmission (réception
// ou dépôt dans le sas), triée côté API.

export type ReceptionItem =
  paths["/api/yards/{yardId}/receptions"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

type Transmission = {
  key: string;
  bordereauNumber: string | null;
  bordereauFichierId: number | null;
  dateReception: string | null;
  items: ReceptionItem[];
};

export type EmetteurGroup = {
  emetteurContractId: number;
  emetteurName: string | null;
  rowCount: number;
  transmissions: Transmission[];
};

function transmissionKey(item: ReceptionItem): string {
  return isNotNil(item.receptionId)
    ? `reception-${item.receptionId}`
    : `depot-${item.dateReception ?? "inconnu"}`;
}

/** Groups consecutive (already sorted) items by émetteur, then transmission. */
export function groupReceptions(items: ReceptionItem[]): EmetteurGroup[] {
  const groups: EmetteurGroup[] = [];
  for (const item of items) {
    let group = groups.at(-1);
    if (group?.emetteurContractId !== item.emetteurContractId) {
      group = {
        emetteurContractId: item.emetteurContractId,
        emetteurName: item.emetteurName,
        rowCount: 0,
        transmissions: [],
      };
      groups.push(group);
    }
    const key = transmissionKey(item);
    let transmission = group.transmissions.at(-1);
    if (transmission?.key !== key) {
      transmission = {
        key,
        bordereauNumber: item.bordereauNumber,
        bordereauFichierId: item.bordereauFichierId,
        dateReception: item.dateReception,
        items: [],
      };
      group.transmissions.push(transmission);
    }
    transmission.items.push(item);
    group.rowCount += 1;
  }
  return groups;
}

export function formatsLabel(item: ReceptionItem): string {
  const formats: string[] = [];
  if (isNotNil(item.paperCopies)) formats.push(`Papier x${item.paperCopies}`);
  if (item.hasPdf) formats.push("PDF");
  if (item.hasNative) formats.push("Natif");
  return formats.join(" / ");
}

function formatDate(iso: string | null): string {
  return isNil(iso) ? "-" : formatDay(iso);
}

function filterItems(
  items: ReceptionItem[],
  activeFilters: FilterValues,
): ReceptionItem[] {
  const emetteurs = activeFilters.prestataire ?? [];
  const lots = activeFilters.lot ?? [];
  const places = activeFilters.zone ?? [];
  return items.filter(
    (item) =>
      (emetteurs.length === 0 ||
        emetteurs.includes(String(item.emetteurContractId))) &&
      (lots.length === 0 ||
        item.lots.some((lot) => lots.includes(String(lot.id)))) &&
      (places.length === 0 ||
        item.places.some((place) => places.includes(place.id))),
  );
}

type FetchState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; items: ReceptionItem[] };

const cellClass = "truncate px-2 text-center";
const headCellClass =
  "bg-zinc-50 px-2 text-center font-medium dark:bg-zinc-800";

export function ClasseurReceptions() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [pdfFile, setPdfFile] = useState<PdfViewerFile | null>(null);
  const { headerRef, style: stickyStyle } = useStickyHeaderOffsets();

  useEffect(() => {
    async function fetchData() {
      try {
        const { data } = await apiClient.GET("/api/yards/{yardId}/receptions", {
          params: { path: { yardId: yardId ?? "" } },
        });
        setState(
          isNotNil(data)
            ? { status: "success", items: data.data.items }
            : { status: "error" },
        );
      } catch (error) {
        console.error("Failed to load reception history", error);
        setState({ status: "error" });
      }
    }
    fetchData();
  }, [yardId]);

  const items = state.status === "success" ? state.items : [];

  // Options come from the full list so they stay stable while filtering.
  const filters = useMemo<FilterDef[]>(() => {
    const emetteurs = new Map<string, string>();
    const lots = new Map<string, string>();
    const places = new Map<string, string>();
    for (const item of items) {
      emetteurs.set(
        String(item.emetteurContractId),
        item.emetteurName ?? String(item.emetteurContractId),
      );
      for (const lot of item.lots) lots.set(String(lot.id), lot.label);
      for (const place of item.places) places.set(place.id, place.label);
    }
    return [
      {
        key: "prestataire",
        label: "Prestataire",
        options: [...emetteurs].map(([value, label]) => ({ value, label })),
      },
      {
        key: "lot",
        label: "Lot",
        options: [...lots].map(([value, label]) => ({ value, label })),
      },
      {
        key: "zone",
        label: "Zone / Bâtiment",
        options: [...places]
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) =>
            a.label.localeCompare(b.label, "fr", { numeric: true }),
          ),
      },
    ];
  }, [items]);

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const visibleItems = useMemo(
    () => filterItems(items, activeFilters),
    [items, activeFilters],
  );
  const groups = useMemo(() => groupReceptions(visibleItems), [visibleItems]);

  async function fetchFile(fichierId: number): Promise<PdfViewerFile | null> {
    try {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        {
          params: {
            path: { yardId: yardId ?? "", fichierId: String(fichierId) },
          },
        },
      );
      if (isNotNil(data)) return data.data;
    } catch (error) {
      console.error("Failed to get a file link", error);
    }
    toaster.error({
      title: "Erreur",
      description: "Impossible d'ouvrir le fichier",
    });
    return null;
  }

  async function openBordereau(fichierId: number) {
    const file = await fetchFile(fichierId);
    if (isNotNil(file)) window.open(file.inlineUrl, "_blank");
  }

  async function openPdf(fichierId: number) {
    setPdfFile(await fetchFile(fichierId));
  }

  async function downloadFile(fichierId: number) {
    const file = await fetchFile(fichierId);
    if (isNotNil(file)) window.open(file.url, "_blank");
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="shrink-0 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Historique des réceptions
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {visibleItems.length > 1
              ? `${visibleItems.length} documents reçus`
              : `${visibleItems.length} document reçu`}
          </p>
        )}
      </header>

      <ClasseurFilterBar
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={onFilterChange}
      />

      <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
        {state.status === "loading" ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Chargement de l'historique…
          </p>
        ) : state.status === "error" ? (
          <p className="py-12 text-center text-sm text-red-600">
            Impossible de charger l'historique des réceptions.
          </p>
        ) : groups.length === 0 ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Aucune réception
          </p>
        ) : (
          // The frame is the scroller, and the cells carry the row lines: a header
          // pinned to it stays where it rests, with nothing scrolling over it.
          <div
            className="min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-zinc-200 dark:border-zinc-700"
            style={stickyStyle}
          >
            <table className="w-full table-fixed border-separate border-spacing-0 text-sm [&_td]:border-b [&_td]:border-zinc-100 dark:[&_td]:border-zinc-800 [&_th]:border-b [&_th]:border-zinc-200 dark:[&_th]:border-zinc-700">
              {/* Widths: .agents/screen-column-widths.md, écran 010. Plain
                  percentages: Chrome and WebKit ignore calc() on <col>. */}
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[15%]" />
                <col className="w-24" />
                <col className="w-[14%]" />
                <col className="w-[9%]" />
                <col className="w-[14%]" />
                <col className="w-16" />
                <col className="w-44" />
              </colgroup>
              <thead ref={headerRef}>
                <tr className="h-8">
                  <th
                    rowSpan={2}
                    className={cn(headCellClass, stickyHeadFirstRowClass)}
                  >
                    Émetteur
                  </th>
                  <th
                    colSpan={2}
                    className={cn(headCellClass, stickyHeadFirstRowClass)}
                  >
                    Transmission
                  </th>
                  <th
                    colSpan={5}
                    className={cn(headCellClass, stickyHeadFirstRowClass)}
                  >
                    Documents joints
                  </th>
                </tr>
                <tr className="h-8">
                  {[
                    "Bordereau joint",
                    "Date",
                    "Lot",
                    "N°",
                    "Intitulé",
                    "Indice",
                    "Formats",
                  ].map((title) => (
                    <th
                      key={title}
                      className={cn(headCellClass, stickyHeadSecondRowClass)}
                    >
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <Fragment key={group.emetteurContractId}>
                    {group.transmissions.map((transmission, tIndex) =>
                      transmission.items.map((item, iIndex) => {
                        const lots = item.lots.map((l) => l.label).join(", ");
                        const formats = formatsLabel(item);
                        return (
                          <tr
                            key={`${transmission.key}-${item.versionId}`}
                            className="h-10"
                          >
                            {tIndex === 0 && iIndex === 0 && (
                              <td
                                rowSpan={group.rowCount}
                                className="border-r border-zinc-100 px-2 text-center align-middle dark:border-zinc-800"
                              >
                                <div
                                  className="truncate"
                                  title={group.emetteurName ?? undefined}
                                >
                                  {group.emetteurName ?? "-"}
                                </div>
                              </td>
                            )}
                            {iIndex === 0 && (
                              <>
                                <td
                                  rowSpan={transmission.items.length}
                                  className="px-2 text-center align-middle"
                                >
                                  <BordereauCell
                                    transmission={transmission}
                                    onOpen={openBordereau}
                                  />
                                </td>
                                <td
                                  rowSpan={transmission.items.length}
                                  className="border-r border-zinc-100 px-2 text-center align-middle dark:border-zinc-800"
                                >
                                  {formatDate(transmission.dateReception)}
                                </td>
                              </>
                            )}
                            <td className={cellClass} title={lots}>
                              {lots || "-"}
                            </td>
                            <td
                              className={cellClass}
                              title={item.documentNumber}
                            >
                              {item.documentNumber}
                            </td>
                            <td className={cellClass} title={item.documentName}>
                              {item.documentName}
                            </td>
                            <td className={cellClass}>
                              {item.versionIndex ?? "-"}
                            </td>
                            <td className={cellClass} title={formats}>
                              <FormatsCell
                                item={item}
                                onOpenPdf={openPdf}
                                onDownload={downloadFile}
                              />
                            </td>
                          </tr>
                        );
                      }),
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {isNotNil(pdfFile) && (
        <PdfViewer file={pdfFile} onClose={() => setPdfFile(null)} />
      )}
    </div>
  );
}

function FormatsCell({
  item,
  onOpenPdf,
  onDownload,
}: {
  item: ReceptionItem;
  onOpenPdf: (fichierId: number) => void;
  onDownload: (fichierId: number) => void;
}) {
  const document = `${item.documentNumber} indice ${item.versionIndex ?? "-"}`;
  const { fichierPdfId, fichierNativeId } = item;
  const parts: ReactNode[] = [];
  if (isNotNil(item.paperCopies)) parts.push(`Papier x${item.paperCopies}`);
  if (isNotNil(fichierPdfId)) {
    parts.push(
      <button
        key="pdf"
        type="button"
        onClick={() => onOpenPdf(fichierPdfId)}
        aria-label={`Ouvrir le PDF de ${document}`}
        className="text-blue-600 hover:underline dark:text-blue-400"
      >
        PDF
      </button>,
    );
  }
  if (isNotNil(fichierNativeId)) {
    parts.push(
      <button
        key="native"
        type="button"
        onClick={() => onDownload(fichierNativeId)}
        aria-label={`Télécharger le fichier natif de ${document}`}
        className="text-blue-600 hover:underline dark:text-blue-400"
      >
        Natif
      </button>,
    );
  }
  if (parts.length === 0) return "-";
  return parts.flatMap((part, index) => (index === 0 ? [part] : [" / ", part]));
}

function BordereauCell({
  transmission,
  onOpen,
}: {
  transmission: Transmission;
  onOpen: (fichierId: number) => void;
}) {
  const { bordereauNumber, bordereauFichierId } = transmission;
  if (isNil(bordereauFichierId)) {
    return (
      <div className="truncate" title={bordereauNumber ?? undefined}>
        {bordereauNumber ?? ""}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(bordereauFichierId)}
      title={bordereauNumber ?? "Ouvrir le bordereau"}
      aria-label={
        isNotNil(bordereauNumber)
          ? `Ouvrir le bordereau ${bordereauNumber}`
          : "Ouvrir le bordereau"
      }
      className="inline-flex max-w-full items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
    >
      <FilePdfIcon size={16} className="shrink-0" />
      <span className="truncate">{bordereauNumber}</span>
    </button>
  );
}
